/**
 * Per-language second-channel banks (duplex v2): the user's language is read from their own words (script, then a few
 * stopwords for Latin-script languages — 0ms, no call), and the backchannel/filler/bridge banks are GENERATED in it by
 * one fast-model call, cached on disk per language and topped up in the background (variety grows across sessions).
 * The hardcoded English CHANNEL_BANK is only the cold-start/offline fallback for English — any other language stays
 * SILENT until its bank is ready (never English grease to a Hebrew speaker).
 */
import { mkdir, readFile, writeFile } from 'node:fs/promises';
import { join } from 'node:path';
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';
import { CHANNEL_BANK, type ChannelKind } from './instinct';
import { isLeakSuspect } from './leak';

const log = forComponent('ChannelBanks');

export type LineKind = Exclude<ChannelKind, 'silence'>;
/** `phatic` = greeting/thanks/yes-no openers in this language: a short turn starting with one is answered by the reflex
 *  instantly, so the channel stays silent (never "hmm" after "hi"). Not a spoken kind. */
export type Bank = Record<LineKind | 'phatic', string[]>;
export const LINE_KINDS: LineKind[] = ['backchannel', 'filler', 'bridge'];
const BANK_KEYS: (keyof Bank)[] = [...LINE_KINDS, 'phatic'];
const EN_PHATIC = ['hey', 'hi', 'hello', 'yo', 'hiya', 'morning', 'good morning', 'good evening', 'good afternoon', 'thanks', 'thank you',
  'ok', 'okay', 'yes', 'yeah', 'yep', 'no', 'nope', 'sure', 'bye', 'goodbye', 'how are you', "what's up", 'whats up', 'can you hear me'];
/** The English cold-start bank (spoken kinds + phatic openers). */
export const EN_BANK: Bank = { ...CHANNEL_BANK, phatic: EN_PHATIC };
/** Hebrew, hand-checked (gender-neutral, no niqqud). A generated Hebrew bank came out as nonsense — "תִּקְרָה…" (ceiling)
 *  and "בּוֹקֵר…" as bridges, "בְּהֵמָה" as a greeting — and was spoken on live calls; curated banks are never generated. */
export const HE_BANK: Bank = {
  backchannel: ['אהה', 'כן', 'נכון', 'אוקיי', 'ממ', 'ברור', 'וואו', 'יפה', 'בטח', 'אה, כן'],
  filler: ['ממ…', 'אהה…', 'כן…', 'אממ…', 'ממ, כן…', 'אוקיי…'],
  bridge: ['רגע…', 'שנייה…', 'רק שנייה…', 'רגע אחד…', 'מיד…', 'עוד רגע…', 'אוקיי, רגע…', 'שנייה, רגע…'],
  phatic: ['היי', 'הי', 'שלום', 'אהלן', 'מה נשמע', 'מה קורה', 'תודה', 'תודה רבה', 'כן', 'לא', 'אוקיי', 'בסדר', 'ביי', 'להתראות',
    'בוקר טוב', 'ערב טוב', 'אתה שומע אותי', 'את שומעת אותי'],
};

/** BCP-47 tag → its primary subtag ('he-IL' → 'he'), the key banks and detection use. */
export const primaryLang = (tag: string): string => tag.trim().toLowerCase().split(/[-_]/)[0];

/** Does `text` open with one of `bank`'s phatic phrases (whole words)? */
export function isPhatic(text: string, bank: Bank | null): boolean {
  const t = norm(text);
  return !!t && !!bank?.phatic?.some((p) => { const q = norm(p); return !!q && (t === q || t.startsWith(q + ' ')); });
}

/** What a finished user turn calls for (duplex v3 §3), by intent, not length: 'ask' = a question or request of any length
 *  (a trailing '?', a request/question opener, a forming question — "מה השעה עכשיו בטוקיו" is 4 words and still asks);
 *  'closer' = an acknowledgement or goodbye made only of closer words ("Okay.", "Cool, thanks.", "תודה, סבבה") — no
 *  answer is due, so no fill line; 'say' = anything else (a statement, a greeting, a correction): answered, bridged
 *  late if MAIN is slow. en + he closer words; other languages never read as closers. */
export type TurnIntent = 'ask' | 'say' | 'closer';
const CLOSER = new Set(("ok okay k cool great nice perfect awesome amazing lovely excellent alright fine good thanks thank thx you so much " +
  "very a lot bye goodbye cheers later see ya night got it i that that's thats is it's its all for now sounds understood noted " +
  "makes sense oh ah wow right then just " +
  "תודה רבה סבבה אוקיי אוקי בסדר יופי אחלה מעולה מצוין מגניב נהדר ביי להתראות טוב זהו זה הכל בינתיים הבנתי ממש לך אז").split(' '));
export function turnIntent(text: string): TurnIntent {
  const t = text.trim();
  if (/\?[\s"')\]]*$/.test(t)) return 'ask';
  const w = norm(t).split(' ').filter(Boolean);
  const asks = requestOpener(t) && !/^ת/.test(lead(t)[0] ?? '') || pendingQuestion(t); // ("תודה" is no ת- imperative)
  if (w.length && w.length <= 8 && w.every((x) => CLOSER.has(x)) && !asks) return 'closer';
  return asks || requestOpener(t) ? 'ask' : 'say';
}

/** Turn-shape openers (0ms, en + he; other languages fall to the decider). Live partials carry no '?' and the final's
 *  length is unknown mid-turn, so a question or command is read from how the utterance OPENS, past discourse fillers and
 *  transcript speaker labels ("Me:", "Speaker 1:"). Replay (tests/voice-bc-anticipate-eval.ts): 62/129 reactive
 *  backchannels landed on questions / short commands — "mm-hm" to "can you outline the most important things…". */
const SKIP = new Set(('um uh uhm erm hmm mm oh ok okay so no yes yeah yep well hey hi and but right alright actually like now then wait ' +
  'hang on me speaker אוקיי טוב אז לא כן רגע אה אמ יאללה ו').split(' '));
const ASK = ('what whats how why where when who which whose is are was were do does did can could would will should shall have has ' +
  'may isnt arent dont doesnt didnt cant wont check open show tell find search look send run go lets please take give make write explain select ' +
  'focus order try compare list use set activate highlight outline capture start stop read play call book create add remove delete put ' +
  'type click close switch help get name recommend suggest summarize translate mark keep remind schedule מה איך למה איפה מתי מי האם כמה תן תני תגיד תגידי בוא בואי').split(' ');
const WANT = /^(i (want|need|would like|d like|wanna)|אני (רוצה|צריך|צריכה|מבקש|מבקשת))(\s|$)/u;
const lead = (text: string): string[] => {
  const w = text.toLowerCase().replace(/[^\p{L}\p{N}\s]+/gu, '').split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < w.length && (SKIP.has(w[i]) || /^\d+$/.test(w[i]))) i++;
  return w.slice(i);
};
/** Does the utterance open as a question or a request/command ("can you…", "check…", "I want you to…", "תפתח…")? */
export function requestOpener(text: string): boolean {
  const w = lead(text);
  if (!w.length) return false;
  return ASK.includes(w[0]) || WANT.test(w.slice(0, 4).join(' ')) || /^ת\p{Script=Hebrew}{2,}$/u.test(w[0]); // Hebrew 2nd-person future = the spoken imperative
}
/** A question is forming in the last few words ("…can you find", "…is there a"), or a wh-question opened this sentence
 *  ("what do you think we should change in the pipeline" — STT partials carry no "?") — never "mm-hm" into it. */
export function pendingQuestion(text: string, lastWords = 6): boolean {
  const all = text.toLowerCase().replace(/[^\p{L}\p{N}'\s.!?]+/gu, ' ');
  const sentence = all.split(/[.!?]/).at(-1)!.split(/\s+/).filter(Boolean);
  const t = sentence.slice(-lastWords).join(' ');
  return /\b(can|could|would|will|do|does|did|is|are|should)\s+(you|we|i|it|there|they|he|she)\b/.test(t) || /(^|\s)(האם|אתה יכול|את יכולה)(\s|$)/u.test(t) ||
    /\b(what|why|how|where|when|who|which)\s+(do|does|did|is|are|should|would|can|could|will)\s+(you|we|i|it|they|he|she)\b/.test(sentence.slice(-4 * lastWords).join(' '));
}

/** Script → language. Latin is resolved by stopwords below; a script shared by several languages maps to its main one. */
const SCRIPTS: [RegExp, string][] = [
  [/\p{Script=Hebrew}/gu, 'he'], [/\p{Script=Arabic}/gu, 'ar'], [/\p{Script=Cyrillic}/gu, 'ru'], [/\p{Script=Greek}/gu, 'el'],
  [/[\p{Script=Hiragana}\p{Script=Katakana}]/gu, 'ja'], [/\p{Script=Hangul}/gu, 'ko'], [/\p{Script=Han}/gu, 'zh'],
  [/\p{Script=Thai}/gu, 'th'], [/\p{Script=Devanagari}/gu, 'hi'], [/\p{Script=Latin}/gu, 'latin'],
];
const STOP: Record<string, Set<string>> = Object.fromEntries(Object.entries({
  en: 'hi hello hey yes no a thanks let see check one sec just give right well look the and is are you i it to of that what this was have with for not but my me', // 'a'/'no' are English too: without them 'thanks a lot', 'long time no see' failed fitsLanguage('en')
  es: 'hola sí gracias bueno a me te lo se le del al el la los las que es y de en un una por para con no pero mi yo qué está',
  fr: 'bonjour salut oui merci le la les est et de des un une que je tu il pas pour avec mais mon qui',
  de: 'hallo ja danke der die das und ist ich du nicht ein eine mit zu was aber mein wie auch',
  it: 'ciao grazie sì a mi ti si il lo la gli che è e di un una non per con ma mio io sono',
  pt: 'olá oi sim obrigado obrigada o a os as que é e de em um uma não para com mas meu eu você',
  nl: 'hoi dank de het een en is ik je niet van dat met voor maar mijn wat',
}).map(([k, v]) => [k, new Set(v.split(' '))]));

const scriptOf = (w: string) => SCRIPTS.find(([re]) => { re.lastIndex = 0; return re.test(w); })?.[1] ?? null;

/** The language of `text` (BCP-47 primary code), or null when there is too little to tell. The majority script by WORDS
 *  wins, so a Hebrew sentence with English tech words is Hebrew. Latin needs a clear stopword lead: ≥2 hits, or 1 hit
 *  in a ≤3-word utterance ("hola", "sí") with no rival hit. `switching` (a language is already known): only strong
 *  evidence — ≥2 non-Latin words, or a Latin lead of ≥2 hits over the runner-up in ≥4 words — so a hesitation that is
 *  a stopword elsewhere ("Um," = pt 'um') never flips an English session. */
export function detectLanguage(text: string, switching = false): string | null {
  const ws = text.toLowerCase().match(/\p{L}+/gu) ?? [];
  const n = new Map<string, number>();
  for (const w of ws) { const s = scriptOf(w); if (s) n.set(s, (n.get(s) ?? 0) + 1); }
  const [best, c] = [...n].sort((a, b) => b[1] - a[1])[0] ?? [null, 0];
  if (best !== 'latin') return c >= 1 && (best === 'zh' || best === 'ja' || c >= 2 || (ws.length === 1 && !switching)) ? best : null;
  const score = Object.entries(STOP).map(([l, s]) => [l, ws.filter((w) => s.has(w)).length] as const).sort((a, b) => b[1] - a[1]);
  const [[l, a], [, b]] = score;
  if (switching) return a - b >= 2 && ws.length >= 4 ? l : null;
  return a > b && (a >= 2 || (a === 1 && ws.length <= 3)) ? l : null;
}

const NAMES = new Intl.DisplayNames(['en'], { type: 'language' });
export const languageName = (lang: string): string => { try { return NAMES.of(lang) ?? lang; } catch { return lang; } };

/** Does `line` fit `lang`? Non-Latin languages: written in their script, no Latin word. Latin ones: no stopword of
 *  another Latin language that `lang` lacks (a model drifting back to English is caught even in "let me check"); a
 *  cue with no stopword ("mm-hm") cannot be told and passes. */
export function fitsLanguage(line: string, lang: string): boolean {
  const own = SCRIPTS.find(([, l]) => l === lang)?.[0];
  if (own) return (line.match(own)?.length ?? 0) > 0 && !/\p{Script=Latin}{2,}/u.test(line);
  if (/[^\p{Script=Latin}\p{M}\p{P}\p{Z}\p{N}\p{S}]/u.test(line)) return false;
  const mine = STOP[lang];
  const ws = line.toLowerCase().match(/\p{L}+/gu) ?? [];
  return !ws.some((w) => !mine?.has(w) && Object.entries(STOP).some(([l, s]) => l !== lang && s.has(w)));
}

/** Unicode-aware compare key (the old [^a-z] key collapsed every Hebrew line to ''). */
export const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}' ]/gu, '').replace(/\s+/g, ' ').trim();

/** One spoken line, ≤`maxWords` words, no quotes/labels, no meta, in `lang`. '' = rejected. */
export function cleanLine(s: string, lang?: string, maxWords = 6): string {
  const raw = s.trim().split('\n')[0];
  const l = raw.replace(/^[-*•\d.)\s]+/, '').replace(/^["'“”«»]+|["'“”«»]+$/g, '').replace(/^\p{L}+:\s*/u, '').trim();
  if (!l || isLeakSuspect(raw) || isLeakSuspect(l)) { if (l) log.debug(`line "${raw}" reads as meta — rejected`); return ''; }
  if (lang && !fitsLanguage(l, lang)) { log.debug(`line "${l}" is not ${lang} — rejected`); return ''; }
  return l.split(/\s+/).length > maxWords || l.length > 60 ? '' : l;
}

/** English discourse markers / turn openers: a speaker's words, not a listener's sound. The generator was TOLD so (KIND_HINT)
 *  and still wrote them into the cached v2 bank as fillers — spoken live as "anyway…", "so…", "basically…" (20261013-152733).
 *  Enforced here so a cached or freshly generated bank can't carry them. */
const SPEAKER_FILLER = /^(?:so|well|actually|basically|anyway|anyways|like|you know|i mean|kind of|sort of|literally|honestly|and|but|because|then|the thing is)\b/i;

/** A generator's raw bank → a valid one (cleaned, deduped, in-language), or null when any kind is too thin. */
export function validateBank(raw: unknown, lang: string, minPerKind = 3): Bank | null {
  if (!raw || typeof raw !== 'object') return null;
  const out = {} as Bank;
  for (const k of BANK_KEYS) {
    const v = (raw as Record<string, unknown>)[k];
    const seen = new Set<string>();
    out[k] = (Array.isArray(v) ? v : []).filter((x): x is string => typeof x === 'string').map((x) => cleanLine(x, lang))
      .filter((x) => x && !(k === 'filler' && SPEAKER_FILLER.test(norm(x))) && !seen.has(norm(x)) && seen.add(norm(x)));
    if (out[k].length < minPerKind) { log.warn(`bank ${lang}: only ${out[k].length} valid ${k} lines — rejected`); return null; }
  }
  return out;
}

const KIND_HINT: Record<keyof Bank, string> = {
  backchannel: 'tiny listener cues while they keep talking (like "mm-hm", "right") — never takes the turn',
  filler: 'soft LISTENER sounds while they pause mid-thought (like "mm…", "mhm", "yeah…") — never a word that continues or completes their sentence ("kind of", "basically", "you know") and never the start of yours',
  bridge: 'neutral one-moment cues while the answer is prepared (like "let me see…", "one sec…") — never the start of a statement or opinion ("the thing is", "well actually", "here\'s the deal")',
  phatic: 'greetings, thanks and yes/no openers a person starts a turn with (like "hi", "thanks", "how are you")',
};

/** One fast-model call → a raw bank for `lang` (validated by ChannelBanks). `avoid` = lines already cached (variety). */
/** The voice's gender — the persona speaks of itself in it ('' = unknown: gender-neutral wording, told so explicitly —
 *  left alone, a Hebrew line defaulted to the masculine "רגע, בודק…"). */
export type Gender = 'female' | 'male' | '';
/** A prompt line that fixes the speaker's grammatical gender (Hebrew, Arabic, Russian… mark it on verbs and adjectives:
 *  a fill line said "בודק" while MAIN, on the same female voice, said "יכולה" — bare-browser #938). */
export function personaHint(gender: Gender | undefined): string {
  return gender === 'female' ? 'You are a woman: when you refer to yourself, use feminine grammatical forms (e.g. Hebrew "בודקת", not "בודק").'
    : gender === 'male' ? 'You are a man: when you refer to yourself, use masculine grammatical forms (e.g. Hebrew "בודק", not "בודקת").'
    : 'Your gender is unknown: never refer to yourself in a gendered form (in Hebrew use plural "בודקים" or no verb at all, never "בודק"/"בודקת").';
}

export function fastBankGenerator(ai: ChatLike, model: string, perKind = 12): ChannelBanksOptions['generate'] {
  return async (lang, avoid, signal) => {
    const name = languageName(lang);
    const prompt = `Write short spoken side-sounds for a live voice conversation, in ${name} (natural, casual, how native ${name} speakers really talk — not translated English). ` +
      `${perKind} varied lines per kind, 1-4 words each — short, idiomatic, grammatical everyday phrases a native would actually say:\n${BANK_KEYS.map((k) => `- ${k}: ${KIND_HINT[k]}`).join('\n')}\n` +
      (avoid.length ? `Do not repeat these existing ones: ${avoid.slice(0, 60).join(' | ')}\n` : '') +
      `Reply with JSON only: {"backchannel":[...],"filler":[...],"bridge":[...],"phatic":[...]}. Every line in ${name}; no explanations.`;
    const reasoning = /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: 2000, ...reasoning })) as ChatResponse;
    const m = (r.content ?? '').match(/\{[\s\S]*\}/);
    if (!m) throw new Error(`no JSON in bank reply: ${(r.content ?? '').slice(0, 80)}`);
    return JSON.parse(m[0]);
  };
}

export class ChannelBanksOptions {
  /** Raw bank for a language (fastBankGenerator). Absent → English fallback only. */
  generate?: (lang: string, avoid: string[], signal: AbortSignal) => Promise<unknown>;
  /** Disk cache dir (one `<lang>.json` per language). Absent → in-memory only. */
  cacheDir?: string;
  /** A cached bank older than this is still used, and topped up by one background call. */
  refreshMs = 24 * 3600_000;
  /** Lines kept per kind (newest win) — the cap on growth. */
  maxPerKind = 40;
  timeoutMs = 20_000;
  /** After a failed cold generation, the next attempt waits this long. */
  retryMs = 60_000;
  /** Cold-start/offline banks (English only by default). */
  fallback: Record<string, Bank> = { en: EN_BANK };
  /** Hand-checked banks: used as-is for their language — never generated, cached or topped up. */
  curated: Record<string, Bank> = { he: HE_BANK };
  now: () => number = () => Date.now();
}

/** Bumped when the bank's WORDING rules change: an older cache is dropped (not merged — its lines are what changed).
 *  v2: fillers/bridges are listener cues, never sentence starters ("the thing is—", "kind of…" spoken live). */
const BANK_VERSION = 2;
interface Entry { at: number; v?: number; bank: Bank }

export class ChannelBanks {
  private banks = new Map<string, Bank>();
  private pending = new Map<string, Promise<void>>();

  public constructor(public options?: Partial<ChannelBanksOptions>) {
    this.options = { ...new ChannelBanksOptions(), ...options };
  }
  private get o(): ChannelBanksOptions { return this.options as ChannelBanksOptions; }

  /** The bank to speak from for `lang` now — generated/cached, else the fallback, else null (= stay silent). */
  get(lang: string): Bank | null { return this.o.curated[lang] ?? this.banks.get(lang) ?? this.o.fallback[lang] ?? null; }

  /** Load (cache) and/or generate `lang`'s bank in the background. Never throws, never blocks the caller. */
  ensure(lang: string): Promise<void> {
    if (this.o.curated[lang]) return Promise.resolve();
    if (!this.pending.has(lang)) this.pending.set(lang, this.load(lang));
    return this.pending.get(lang)!;
  }

  private file(lang: string): string | null { return this.o.cacheDir ? join(this.o.cacheDir, `${lang.replace(/[^\w-]/g, '')}.json`) : null; }

  private async load(lang: string): Promise<void> {
    const f = this.file(lang);
    let cached: Entry | null = null;
    if (f) cached = await readFile(f, 'utf8').then((s) => JSON.parse(s) as Entry).catch((e) => { if (e.code !== 'ENOENT') log.warn(`bank cache ${f} unreadable — regenerating: ${e.message}`); return null; });
    if (cached && cached.v !== BANK_VERSION) { log.debug(`bank ${lang}: cache v${cached.v ?? 1} predates v${BANK_VERSION} — regenerating`); cached = null; }
    // minPerKind 0: a cache written before a kind existed (pre-`phatic` files) keeps the lines it has — rejecting it
    // threw the whole cached bank away with a warning; the missing kind is borrowed from the fallback and the bank
    // is refreshed now instead of waiting out refreshMs.
    const valid = cached && validateBank(cached.bank, lang, 0);
    const complete = !!valid && BANK_KEYS.every((k) => valid[k].length);
    if (valid) {
      for (const k of BANK_KEYS) if (!valid[k].length) valid[k] = [...(this.o.fallback[lang]?.[k] ?? [])];
      this.banks.set(lang, valid);
      log.debug(`bank ${lang}: cache hit (${BANK_KEYS.map((k) => valid[k].length).join('/')})${complete ? '' : ' — missing kinds, refreshing'}`);
    }
    if (complete && this.o.now() - cached!.at < this.o.refreshMs) return;
    await this.generate(lang, valid);
  }

  private async generate(lang: string, have: Bank | null): Promise<void> {
    if (!this.o.generate) return;
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), this.o.timeoutMs);
    const t0 = this.o.now();
    try {
      const fresh = validateBank(await this.o.generate(lang, have ? BANK_KEYS.flatMap((k) => have[k]) : [], ac.signal), lang);
      if (!fresh) return;
      const bank = {} as Bank;
      for (const k of BANK_KEYS) {
        const seen = new Set<string>();
        bank[k] = [...fresh[k], ...(have?.[k] ?? [])].filter((x) => !seen.has(norm(x)) && seen.add(norm(x))).slice(0, this.o.maxPerKind);
      }
      this.banks.set(lang, bank);
      log.debug(`bank ${lang}: ${have ? 'refreshed' : 'generated'} (${BANK_KEYS.map((k) => bank[k].length).join('/')}) in ${Math.round(this.o.now() - t0)}ms`);
      const f = this.file(lang);
      if (f) await mkdir(this.o.cacheDir!, { recursive: true }).then(() => writeFile(f, JSON.stringify({ at: this.o.now(), v: BANK_VERSION, bank } satisfies Entry))).catch((e) => log.warn(`bank cache write ${f} failed: ${e.message}`));
    } catch (e) {
      log.warn(`bank ${lang}: generation failed — ${have ? 'keeping the cached bank' : lang in this.o.fallback ? 'fallback bank' : 'silent'}: ${(e as Error).message}`);
    } finally {
      clearTimeout(t);
      if (!this.banks.has(lang)) setTimeout(() => this.pending.delete(lang), this.o.retryMs).unref?.(); // failed cold → retry later, not per line
    }
  }
}
